import { useAppSelector, useAppDispatch } from "../redux/hooks";
import { useState, useCallback, useEffect, useRef, memo } from "react";
import { useNavigation } from "../hooks/useNavigation";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import widgetRegistry, { WidgetType } from "../widgets/widgetRegistry";
import {
  updateWidgetData,
  updateSiteContentRequest,
  changeWidgetType,
} from "../redux/slices/siteContentSlice";
import { current } from '@reduxjs/toolkit';
import { toggleEditMode } from "../redux/slices/editModeSlice";
import { toggleNavEditMode } from "../redux/slices/navEditModeSlice";
import { toggleFooterEditMode } from "../redux/slices/footerEditModeSlice";
import { addToCart } from "../redux/slices/cartSlice";
import { useMenuItemCart } from "../hooks/useMenuItemCart";
import { useComboCart } from "../hooks/useComboCart";
import { useCartQuantityControls } from "../hooks/useCartQuantityControls";
import { WidgetSelectionModal, FloatingActionButtons, ConfirmDeleteModal } from "../components";
import { AddToCartModal } from "../components/AddToCartModal";
import { RepeatCustomisationModal } from "../components/RepeatCustomisationModal";
import { DeleteVariantModal } from "../components/DeleteVariantModal";
import { ComboDetailsModal } from "../components/ComboDetailsModal";
import { ItemDetailsModal } from "../components/ItemDetailsModal";
import { parsePrice } from "../utils/priceUtils";
import { createCartItem, formatPreviousCustomizations } from "../utils/cartUtils";
import WidgetStyleSelectionModal from "../components/WidgetStyleSelectionModal";
import { useMenuAndComboData } from "../hooks/useMenuAndComboData";
import { useWidgetDataUpdater } from "../hooks/useWidgetDataUpdater";
import { useWidgetSections } from "../hooks/useWidgetSections";
import { shouldShowAddDeleteControls } from "../utils/widgetVisibilityUtils";

// ---------------- Widget Renderer ----------------
const WidgetRenderer = memo(function WidgetRenderer({
  widget,
  index,
  isEditing,
  onUpdate,
  onNavClick,
  onLogoClick,
  onBannerButtonClick,
  onAddToCart,
  onComboClick,
  onItemClick,
  onIncrementQuantity,
  onDecrementQuantity,
  cartItems,
  onChangeLayout,
}: {
  widget: any;
  index: number;
  isEditing: boolean;
  onUpdate: (newData: any) => void;
  onNavClick?: (item: any) => void;
  onLogoClick?: () => void;
  onBannerButtonClick?: (button: any, index: number) => void;
  onAddToCart?: (item: any) => void;
  onComboClick?: (combo: any) => void;
  onItemClick?: (item: any) => void;
  onIncrementQuantity?: (item: any) => void;
  onDecrementQuantity?: (item: any) => void;
  cartItems?: any[];
  onChangeLayout?: () => void;
}) {
  const { type, data } = widget;
  
  if (!type) {
    return (
      <div
        key={`fallback-${index}`}
        className="p-8 bg-gray-100 border border-gray-300 rounded-lg text-center m-4"
      >
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Widget Not Found</h3>
        <p className="text-gray-600 mb-4">Widget type is missing</p>
        <details className="text-left">
          <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
            Show widget data
          </summary>
          <pre className="mt-2 p-2 bg-gray-200 rounded text-xs overflow-auto">
            {JSON.stringify(data, null, 2)}
          </pre>
        </details>
      </div>
    );
  }

  // Extract base type from unique type (e.g., "EssentialBanner_A1B2C3D4" -> "EssentialBanner")
  const baseType = type.includes('_') ? type.split('_')[0] : type;
  
  // Use widget registry with base type for lazy loading
  const LazyComponent = widgetRegistry[baseType as WidgetType];

  if (!LazyComponent) {
    return (
      <div
        key={`fallback-${index}`}
        className="p-8 bg-gray-100 border border-gray-300 rounded-lg text-center m-4"
      >
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Widget Not Found</h3>
        <p className="text-gray-600 mb-4">Type: {type}</p>
        <details className="text-left">
          <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
            Show widget data
          </summary>
          <pre className="mt-2 p-2 bg-gray-200 rounded text-xs overflow-auto">
            {JSON.stringify(data, null, 2)}
          </pre>
        </details>
      </div>
    );
  }

  return (
    <div key={`widget-${type}-${index}`} className="w-full">
      <LazyComponent
        data={data}
        isEditing={isEditing}
        onUpdate={onUpdate}
        onChange={onUpdate}
        onNavClick={onNavClick}
        onLogoClick={onLogoClick}
        onBannerButtonClick={onBannerButtonClick}
        onAddToCart={onAddToCart}
        onComboClick={onComboClick}
        onItemClick={onItemClick}
        onIncrementQuantity={onIncrementQuantity}
        onDecrementQuantity={onDecrementQuantity}
        cartItems={cartItems}
        onChangeLayout={onChangeLayout}
      />
    </div>
  );
});

// ---------------- Home Page ----------------
export default function HomePage() {
  const { data: siteContent, fetchLoading, updateLoading } = useAppSelector((state) => state?.siteContent);
  const {isEditMode} = useAppSelector((state) => state?.editMode);
  const { isEditControl } = useAppSelector((state) => state?.editControl);
  const isNavEditMode = useAppSelector((state) => state?.navEditMode?.isNavEditMode);
  const isFooterEditMode = useAppSelector((state) => state?.footerEditMode?.isFooterEditMode);
  const { items: cartItems } = useAppSelector(state => state.cart);
  const dispatch = useAppDispatch();
  
  // Use centralized navigation hook
  const { handleNavClick, handleLogoClick, handleButtonClick } = useNavigation();

  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const [pendingAddIndex, setPendingAddIndex] = useState<number | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | undefined>(undefined);
  const isSaving = useRef(false);
  
  // Loading states for add/delete operations
  const [isSavingAdd, setIsSavingAdd] = useState(false);
  const [isSavingDelete, setIsSavingDelete] = useState(false);
  
  // Widget style selection modal state
  const [showStyleModal, setShowStyleModal] = useState(false);
  const [selectedWidgetForStyle, setSelectedWidgetForStyle] = useState<{ index: number; type: string; data: any } | null>(null);
  const [isSavingStyle, setIsSavingStyle] = useState(false);

  // Combo details modal state
  const [isComboModalOpen, setIsComboModalOpen] = useState(false);
  const [selectedCombo, setSelectedCombo] = useState<any>(null);

  // Item details modal state (for popular items)
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  // 🔄 Use custom hooks for data management
  const { menuData, comboData, menuLoading, comboLoading } = useMenuAndComboData();
  
  // 🔄 Use custom hook for widget data updates
  useWidgetDataUpdater({
    siteContent,
    menuData,
    comboData,
    menuLoading,
    comboLoading,
  });

  // ✨ Use widget sections hook for add/delete operations
  const { addSection, deleteSection, widgets, hasWidgets } = useWidgetSections();

  //  Use processed widgets directly from Redux state
  const allHomeWidgets = siteContent?.homeWidgets ?? [];
  const isReady = !fetchLoading && siteContent !== null;


  // 🔧 Update widget data - Type-based lookup matching Redux logic
  const handleWidgetUpdate = useCallback(
    (displayIndex: number, newData: any) => {
      // Step 1: Get widget from allHomeWidgets using display index
      const homeWidget = allHomeWidgets[displayIndex];
      if (!homeWidget) {
        console.warn('[handleWidgetUpdate] Widget not found at display index:', displayIndex);
        return;
      }
      
      // Step 2: Find that widget's type in siteContent.widgets (the actual Redux array)
      const actualIndex = siteContent?.widgets?.findIndex(
        (w: any) => w.type === homeWidget.type
      );
      
      if (actualIndex === undefined || actualIndex === -1) {
        console.warn('[handleWidgetUpdate] Widget type not found in siteContent.widgets:', homeWidget.type);
        return;
      }
      
      // Step 3: Safely unwrap any Immer proxies
      let cleanData;
      try {
        cleanData = current(newData);
      } catch (error) {
        // If current() fails, it's already a plain object
        cleanData = newData;
      }
      
      // Step 4: Update - Redux will update both widgets & homeWidgets arrays automatically
      dispatch(updateWidgetData({ index: actualIndex, data: cleanData }));
    },
    [dispatch, allHomeWidgets, siteContent]
  );

  // 💾 Save changes - Unified save that coordinates with nav and footer edit modes
  const handleSaveChanges = useCallback(() => {
    if (siteContent && !fetchLoading) {
      isSaving.current = true;
      
      // Toggle off nav edit mode if it's active
      if (isNavEditMode) {
        dispatch(toggleNavEditMode());
      }
      
      // Toggle off footer edit mode if it's active
      if (isFooterEditMode) {
        dispatch(toggleFooterEditMode());
      }
      
      // Save all changes
      dispatch(updateSiteContentRequest());
    }
  }, [dispatch, siteContent, fetchLoading, isNavEditMode, isFooterEditMode]);

  // ✏️ Toggle edit mode
  const handleEditModeToggle = useCallback(() => {
    if (isEditMode) {
      handleSaveChanges();
    } else {
      dispatch(toggleEditMode());
    }
  }, [isEditMode, handleSaveChanges, dispatch]);

  // ➕ Show widget selection modal
  const handleShowAddModal = useCallback((afterIndex?: number) => {
    setPendingAddIndex(afterIndex);
    setShowWidgetModal(true);
  }, []);

  // ✅ Handle widget selection from modal - simplified with hook
  const handleSelectWidget = useCallback((widgetType: string) => {
    addSection(widgetType, pendingAddIndex);
    dispatch(updateSiteContentRequest()); // Save to API immediately
    setIsSavingAdd(true); // Track save state
    // Don't close modal or exit edit mode yet - wait for API success
  }, [addSection, pendingAddIndex, dispatch]);

  // ❌ Close modal
  const handleCloseModal = useCallback(() => {
    setShowWidgetModal(false);
    setPendingAddIndex(undefined);
  }, []);

  // 🗑️ Show delete confirmation modal
  const handleShowDeleteModal = useCallback((displayIndex: number) => {
    setPendingDeleteIndex(displayIndex);
    setShowDeleteModal(true);
  }, []);

  // ✅ Confirm delete - actually delete the section
  const handleConfirmDelete = useCallback(() => {
    if (pendingDeleteIndex !== undefined) {
      deleteSection(pendingDeleteIndex);
    }
    dispatch(updateSiteContentRequest()); // Save to API immediately
    setIsSavingDelete(true); // Track save state
    // Don't close modal or exit edit mode yet - wait for API success
  }, [deleteSection, pendingDeleteIndex, dispatch]);

  // ❌ Cancel delete
  const handleCancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setPendingDeleteIndex(undefined);
  }, []);

  // 🎯 Banner button click handler - navigate to menu page
  const handleBannerButtonClick = useCallback((button: any, index: number) => {
    handleButtonClick('/menu');
  }, [handleButtonClick]);

  // 🛒 Use reusable cart hooks - no auto-open cart
  const { 
    handleAddToCart: handleMenuItemAddToCart, 
    modalProps,
    openModal
  } = useMenuItemCart();

  const { handleComboAddToCart } = useComboCart();

  // 🛒 Use quantity controls for menu items (handles +/- with modals)
  const {
    handleIncrementItemQuantity,
    handleDecrementItemQuantity,
    isRepeatCustomizationModalOpen,
    itemToRepeatOrCustomize,
    isDeleteVariantModalOpen,
    variantsToDelete,
    itemNameForDelete,
    handleRepeatPreviousCustomization,
    handleCustomizeNewVariant,
    handleUpdateQuantity,
    closeRepeatModal,
    closeDeleteModal,
  } = useCartQuantityControls(cartItems, handleMenuItemAddToCart, openModal);

  // 🛒 Main add to cart handler - handles both menu items and combo items
  const handleAddToCart = useCallback((item: any) => {
    // Check if this is a combo item (has combo_id and title)
    const isComboItem = item.combo_id && item.title;
    
    if (isComboItem) {
      // Use combo hook - preserves current behavior (auto-opens cart)
      handleComboAddToCart(item);
    } else {
      // Use menu item hook for regular items
      handleMenuItemAddToCart(item);
    }
  }, [handleComboAddToCart, handleMenuItemAddToCart]);

  // 🍱 Handle combo card click - open modal
  const handleComboClick = useCallback((combo: any) => {
    setSelectedCombo(combo);
    setIsComboModalOpen(true);
  }, []);

  // 🍱 Handle combo add to cart from modal
  const handleComboAddToCartFromModal = useCallback((combo: any, quantity: number) => {
    // Add multiple quantities
    for (let i = 0; i < quantity; i++) {
      handleComboAddToCart(combo);
    }
  }, [handleComboAddToCart]);

  // 📋 Handle popular item click - open ItemDetailsModal
  const handlePopularItemClick = useCallback((item: any) => {
    setSelectedItem(item);
    setIsItemModalOpen(true);
  }, []);

  // 📋 Handle item details modal add to cart (same logic as MenuPage)
  const handleItemDetailsAddToCart = useCallback((
    item: any,
    quantity: number,
    spiceLevel: number | null,
    modifiers: any[]
  ) => {
    const cartItem = createCartItem(item, spiceLevel, modifiers, quantity);
    dispatch(addToCart(cartItem));
    // Close modal and clear state
    setIsItemModalOpen(false);
    setSelectedItem(null);
  }, [dispatch]);

  // ✅ Close add modal after API success
  useEffect(() => {
    if (isSavingAdd && !updateLoading) {
      setIsSavingAdd(false);
      setShowWidgetModal(false);
      setPendingAddIndex(undefined);
      dispatch(toggleEditMode());
    }
  }, [isSavingAdd, updateLoading, dispatch]);

  // ✅ Close delete modal after API success
  useEffect(() => {
    if (isSavingDelete && !updateLoading) {
      setIsSavingDelete(false);
      setShowDeleteModal(false);
      setPendingDeleteIndex(undefined);
      dispatch(toggleEditMode());
    }
  }, [isSavingDelete, updateLoading, dispatch]);

  // 🎨 Handler for widgets to trigger layout change
  const handleWidgetChangeLayout = useCallback((widgetIndex: number) => {
    const widget = allHomeWidgets[widgetIndex];
    if (widget) {
      setSelectedWidgetForStyle({ 
        index: widgetIndex, 
        type: widget.type, 
        data: widget.data 
      });
      setShowStyleModal(true);
    }
  }, [allHomeWidgets]);

  // 🎨 Handle widget style selection
  const handleSelectStyle = useCallback((newVariant: string) => {
    if (selectedWidgetForStyle) {
      // Find actual index in siteContent.widgets array by matching type
      const actualIndex = siteContent?.widgets?.findIndex(
        (w: any) => w.type === selectedWidgetForStyle.type
      );
      
      if (actualIndex === undefined || actualIndex === -1) {
        console.warn('[handleSelectStyle] Widget not found in siteContent.widgets:', selectedWidgetForStyle.type);
        return;
      }
      
      // Update Redux with new variant using actual index
      dispatch(changeWidgetType({
        widgetIndex: actualIndex,
        newVariant: newVariant,
      }));
      // Save to API
      dispatch(updateSiteContentRequest());
      setIsSavingStyle(true);
    }
  }, [selectedWidgetForStyle, siteContent, dispatch]);

  // 🎨 Close style modal after API success
  useEffect(() => {
    if (isSavingStyle && !updateLoading) {
      setIsSavingStyle(false);
      setShowStyleModal(false);
      setSelectedWidgetForStyle(null);
      dispatch(toggleEditMode());
    }
  }, [isSavingStyle, updateLoading, dispatch]);

  // 🎨 Close style modal
  const handleCloseStyleModal = useCallback(() => {
    setShowStyleModal(false);
    setSelectedWidgetForStyle(null);
  }, []);

  // 📝 Exit edit mode after save is completed
  useEffect(() => {
    if (isSaving.current && !updateLoading && !fetchLoading) {
      dispatch(toggleEditMode());
      isSaving.current = false;
    }
  }, [updateLoading, fetchLoading, dispatch]);

  // 🚫 Early returns after all hooks
  if (!isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-3 border-orange-500 border-t-transparent rounded-full mx-auto" />
        </div>
      </div>
    );
  }

  if (!hasWidgets) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 font-medium mb-4">No home widgets available</p>
          {isEditMode && (
            <button
              onClick={() => handleShowAddModal()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add Your First Widget
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="homepage w-full min-h-screen relative">
    
      {/* Unified Floating Action Buttons - Show when edit control is enabled and not in nav edit mode */}
      {!isNavEditMode && isEditControl && (
        <FloatingActionButtons
          isEditMode={isEditMode}
          onEditToggle={handleEditModeToggle}
          disabled={updateLoading || fetchLoading}
          position="bottom-right"
          showBrandPicker={true}
        />
      )}

      {/* Render Widgets */}
      {widgets.map((widget: any, index: number) => (
        <div key={`widget-container-${index}`} className="relative">
          <WidgetRenderer
            widget={widget}
            index={index}
            isEditing={isEditMode}
        onUpdate={(newData) => handleWidgetUpdate(index, newData)}
        onNavClick={handleNavClick}
        onLogoClick={handleLogoClick}
        onBannerButtonClick={handleBannerButtonClick}
        onAddToCart={handleAddToCart}
        onComboClick={handleComboClick}
        onItemClick={handlePopularItemClick}
        onIncrementQuantity={handleIncrementItemQuantity}
        onDecrementQuantity={handleDecrementItemQuantity}
        cartItems={cartItems}
        onChangeLayout={() => handleWidgetChangeLayout(index)}
          />

        
          {isEditMode && shouldShowAddDeleteControls(widget, isEditMode) && (
            <div className="flex justify-center gap-4 py-4 bg-gray-50 border-t border-b border-gray-200">
              <button
                onClick={() => handleShowAddModal(index)}
                disabled={isSavingAdd}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 shadow-lg hover:shadow-xl group disabled:opacity-70 disabled:cursor-not-allowed"
                title="Add section after this one"
              >
                <FiPlus className="w-5 h-5 group-hover:scale-110 transition-transform" />
                <span className="font-medium text-sm">{isSavingAdd ? "Adding..." : "Add Section"}</span>
              </button>

              {widgets.length > 1 && (
                <button
                  onClick={() => handleShowDeleteModal(index)}
                  disabled={isSavingDelete}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 shadow-lg hover:shadow-xl group disabled:opacity-70 disabled:cursor-not-allowed"
                  title="Delete this section"
                >
                  <FiTrash2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span className="font-medium text-sm">{isSavingDelete ? "Deleting..." : "Delete Section"}</span>
                </button>
              )}
            </div>
          )}
        </div>
      ))}


      {/* Widget Selection Modal */}
      <WidgetSelectionModal
        isOpen={showWidgetModal}
        onClose={handleCloseModal}
        onSelectWidget={handleSelectWidget}
        isLoading={isSavingAdd}
      />
      
      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={isSavingDelete}
      />
      
      {/* Add to Cart Modal */}
      <AddToCartModal {...modalProps} />
      
      {/* Widget Style Selection Modal */}
      <WidgetStyleSelectionModal
        isOpen={showStyleModal}
        onClose={handleCloseStyleModal}
        widgetType={selectedWidgetForStyle?.type || ''}
        currentVariant={selectedWidgetForStyle?.type || ''}
        currentData={selectedWidgetForStyle?.data}
        onSelectVariant={handleSelectStyle}
        isLoading={isSavingStyle}
      />

      {/* Repeat Customisation Modal - For all menu items */}
      <RepeatCustomisationModal
        isOpen={isRepeatCustomizationModalOpen}
        itemName={itemToRepeatOrCustomize?.item?.name || ''}
        previousSelections={itemToRepeatOrCustomize ? formatPreviousCustomizations(itemToRepeatOrCustomize) : []}
        spiceLevel={itemToRepeatOrCustomize?.selected?.spice}
        modifiers={itemToRepeatOrCustomize?.selected?.modifiers}
        onRepeat={handleRepeatPreviousCustomization}
        onChoose={handleCustomizeNewVariant}
        onClose={closeRepeatModal}
      />

      {/* Delete Variant Modal - For all menu items */}
      <DeleteVariantModal
        isOpen={isDeleteVariantModalOpen}
        itemName={itemNameForDelete}
        basePrice={variantsToDelete[0]?.item?.price ? parsePrice(variantsToDelete[0].item.price) : 0}
        variants={variantsToDelete}
        onUpdateQuantity={handleUpdateQuantity}
        onClose={closeDeleteModal}
      />

      {/* Combo Details Modal */}
      <ComboDetailsModal
        isOpen={isComboModalOpen}
        combo={selectedCombo}
        onClose={() => setIsComboModalOpen(false)}
        onAddToCart={handleComboAddToCartFromModal}
      />

      {/* Item Details Modal - For popular items */}
      <ItemDetailsModal
        isOpen={isItemModalOpen}
        item={selectedItem}
        onClose={() => setIsItemModalOpen(false)}
        onAddToCart={handleItemDetailsAddToCart}
        menuData={menuData}
        onRecommendedItemClick={handlePopularItemClick}
      />
    </div>
  );
}
